import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import { FC, useEffect, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useLocation, useNavigate } from "react-router";
import { Link } from "react-router-dom";
import countryList from "react-select-country-list";
import { v4 as uuidv4 } from "uuid";
import { ExpiryAlertMsg, STATE_ALIAS, STATE_LINKS } from "../../../constants/app";
import { useImpersonation } from "../../../contexts/ImpersonationContext";
import {
  TQualificationsFormValues,
  TSelectOption,
} from "../../../models/Onboarding/onboarding";
import {
  ALTERNATIVE_POSITIONS,
  COMPLETE_YOUR_STUDIES,
  EARLY_CHILDHOOD_EDUCATION,
  OTHER_QUALIFICATIONS_LEVEL,
} from "../../../models/Onboarding/onboarding-values";
import Roles from "../../../models/roles";
import { AUTH_ROUTE } from "../../../pages/App/routes";
import { CandidateStorage } from "../../../services/CandidateStorage";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import {
  hideTransparentPreloaderAction,
  showNotificationAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import { setMissingDocsCountAction } from "../../../store/onboarding/slice";
import { qualificationsThunk } from "../../../store/onboarding/thunk";
import { TUploadedDocumentsData } from "../../../store/onboarding/types";
import {
  getCandidateData,
  profileEditRequestStatusThunk,
} from "../../../store/superadmin/thunks";
import {
  deleteCandidateDocumentThunk,
  updateDocumentDateThunk,
} from "../../../store/uploads/thunks";
import { TFileUploadsResponse } from "../../../store/uploads/types";
import { createValidationSchema } from "../../../utils/createValidationSchema";
import { getMinDateByDocumentType, getValidityMonths } from "../../../utils/documentHelper";
import { computeMissingDocs } from "../../../utils/missingDocsHelper";
import CustomButton from "../../UI/Buttons/CustomButton";
import RightArrow from "../../UI/Icons/RightArrow";
import RoundedTick from "../../UI/Icons/RoundedTick";
import Warning from "../../UI/Icons/Warning";
import CheckboxInput from "../../UI/Inputs/Checkbox";
import CustomDateInput from "../../UI/Inputs/DateInput";
import DocumentUpload from "../../UI/Inputs/DocumentUpload";
import RadioInput from "../../UI/Inputs/RadioInput";
import CustomSelect from "../../UI/Select";
import ErrorMessage from "../../UI/Tooltips/ErrorMessage";
import PositionTypes from "./PositionTypes";

const Qualifications: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { isImpersonating, impersonationData } = useImpersonation();

  const user = useSelector((state) => state.auth.user);
  // const submitStage = useSelector((state) => state.auth.submitStage);
  const profileData = useSelector((state) => state.onboarding.personaldetails);
  const currentStage = profileData?.current_stage
    ? profileData?.current_stage
    : null;
  const stepCompletion = useSelector(
    (state) => state.onboarding.completedstatus
  );

  // Check if the logged-in user is a Super Admin (used for editing candidate profiles)
  const isSuperAdmin =
    (user?.role?.name as Roles) === Roles.SUPER_ADMIN ||
    (user?.role?.name as Roles) === Roles.GENERAL_ADMIN;

  // const isGeneralAdmin = (user?.role?.name as Roles) === Roles.GENERAL_ADMIN;
  const userpersonaldetails = useSelector((state) =>
    !isSuperAdmin
      ? state.onboarding.userpersonaldetails
      : state.superadmin.reviewCandidateData
  );

  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [checkPath, setCheckPath] = useState<boolean>(false);
  const [missingDocs, setMissingDocs] = useState<string[]>([]);

  const [wccValidUntil, setWccValidUntil] = useState<Date | null>(null);
  const [faValidUntil, setFaValidUntil] = useState<Date | null>(null);
  const [mrtValidUntil, setMrtValidUntil] = useState<Date | null>(null);
  const [trValidUntil, setTrValidUntil] = useState<Date | null>(null);
  const [pgValidUntil, setPgValidUntil] = useState<Date | null>(null);
  const [ugValidUntil, setUgValidUntil] = useState<Date | null>(null);
  const [ciiiValidUntil, setCiiiValidUntil] = useState<Date | null>(null);
  const [fhcValidUntil, setFhcValidUntil] = useState<Date | null>(null);
  const [cqValidUntil, setCqValidUntil] = useState<Date | null>(null);
  const [awvValidUntil, setAwvValidUntil] = useState<Date | null>(null);
  const [prvIssueDate, setPrvIssueDate] = useState<Date | null>(null);
  const [diplValidUntil, setDiplValidUntil] = useState<Date | null>(null);

  const alternativePositions = [
    ALTERNATIVE_POSITIONS.ADMIN_ASSISTANT,
    ALTERNATIVE_POSITIONS.CLEANER,
    ALTERNATIVE_POSITIONS.GARDENER,
    ALTERNATIVE_POSITIONS.CHEF_COOK
  ];

  /** Get the values from the state */
  const qualifications = useSelector(
    (state) =>
      state.onboarding.qualifications ??
      state.superadmin.reviewCandidateData?.qualifications
  );

  const reviewCandidateData = useSelector((state) => state.superadmin.reviewCandidateData)

  const qualificationsDocs = useSelector(
    (state) =>
      state.onboarding.qualifications
  );

  const reviewDocuments = useSelector(
    (state) => state.superadmin.reviewCandidateData?.documents ?? []
  );

  const workingRights = isSuperAdmin ? reviewCandidateData?.profile?.working_rights : profileData?.working_rights;

  // Merge documents into qualifications
  const updatedQualifications = {
    ...qualifications,
    documents: [...reviewDocuments],
  };

  let qualificationDocs: any;
  if(isSuperAdmin) {
    qualificationDocs = updatedQualifications;
  } else {
    qualificationDocs = qualificationsDocs;
  }

  const safeParse = (item: string) => {
    try {
      return item ? JSON.parse(item) : null;
    } catch (error: any) {
      error.message !== "" &&
        error.message !== "Unexpected end of JSON input" &&
        console.error("Error parsing JSON:", error);
      return null;
    }
  };

  const education = sessionStorage.getItem("qualification");
  const educated_location = sessionStorage.getItem(
    "studies_completed_location"
  );
  const currently_equivalent = sessionStorage.getItem(
    "currently_studying_option"
  );
  const other_country =
    sessionStorage.getItem("other_study_selected_country") ?? "";
  const acecqa_app =
    sessionStorage.getItem("acecqa_approved") === "true" ? true : false;
  const qualification_levels_arr: any = sessionStorage.getItem(
    "qualification_accept_options"
  );
  const qualification_levels = safeParse(qualification_levels_arr);
  const certificate_diploma_jobs = sessionStorage.getItem(
    "diploma_jobs_at_certificate_level"
  );
  const alt_positions_arr: any = sessionStorage.getItem(
    "alternative_positions"
  );
  const alt_positions = safeParse(alt_positions_arr);
  const educator_positions_arr: any = sessionStorage.getItem(
    "educator_teacher_positions"
  );
  const educator_teacher_positions = safeParse(educator_positions_arr);

  /* 1.  Alternative positions */
  const [selectedAlternativePosition, setSelectedAlternativePosition] =
    useState<string[]>(
      safeParse(qualifications?.alternative_positions ?? alt_positions ?? "") ??
        []
    );

  /* 2.  Qualification levels the user accepts */
  const [selectedQualificationLevels, setSelectedQualificationLevels] =
    useState<string[]>(
      safeParse(
        qualifications?.qualification_accept_options ??
          qualification_levels ??
          ""
      ) ?? []
    );

  /* 4.  Educator/Teacher Positions: the user accepts */
  const [selectedEducatorPositions, setSelectedEducatorPositions] =
    useState<string[]>(() => {
      const edPos = qualifications?.educator_teacher_positions;
      if (Array.isArray(edPos)) return edPos;
      return safeParse((edPos as string | undefined) ?? educator_teacher_positions ?? "") ?? [];
    });

  /* 3.  ACECQA approval flag */
  const [isQualificationApproved, setIsQualificationApproved] =
    useState<boolean>(
      Boolean(qualifications?.acecqa_approved ?? acecqa_app ?? false)
    );

  /* 4.  Country for “other” study */
  const [selectedCountry, setSelectedCountry] = useState<string>(
    qualifications?.other_study_selected_country ?? other_country ?? ""
  );

  const options: TSelectOption[] = useMemo(() => {
    const countryOptions = countryList().getData();
    return [{ value: "", label: "Select a country" }, ...countryOptions];
  }, []);

  const workingWithChildrenCheckCertificate = qualificationDocs?.documents?.find(
    (doc: TUploadedDocumentsData) =>
      doc.document_type === "working_with_children_check"
  );
  const firstAidCertificate = qualificationDocs?.documents?.find(
    (doc: TUploadedDocumentsData) =>
      doc.document_type === "first_aid_certificate"
  );
  const mandatoryReportingTrainingCertificate = qualificationDocs?.documents?.find(
    (doc: TUploadedDocumentsData) =>
      doc.document_type === "mandatory_reporting_training"
  );

  const postGraduateCertificate = qualificationDocs?.documents?.find(
    (doc: TUploadedDocumentsData) =>
      doc.document_type === "post_graduate_diploma"
  );
  const bachelorOrDegreeCertificate = qualificationDocs?.documents?.find(
    (doc: TUploadedDocumentsData) => doc.document_type === "bachelor_or_degree"
  );
  const certificateIIICertificate = qualificationDocs?.documents?.find(
    (doc: TUploadedDocumentsData) => doc.document_type === "certificate_iii"
  );
  const diplomaCertificate = qualificationDocs?.documents?.find(
    (doc: TUploadedDocumentsData) => doc.document_type === "diploma"
  );
  const foodHandlerCertificate = qualificationDocs?.documents?.find(
    (doc: TUploadedDocumentsData) =>
      doc.document_type === "food_handler_certificate"
  );
  const cookingQualificationCertificate = qualificationDocs?.documents?.find(
    (doc: TUploadedDocumentsData) =>
      doc.document_type === "cooking_qualification"
  );
  const acecqaCertificate = qualificationDocs?.documents?.find(
    (doc: TUploadedDocumentsData) => doc.document_type === "ACECQA_assessment"
  );
  const teachersRegCertificate = qualificationDocs?.documents?.find(
    (doc: TUploadedDocumentsData) =>
      doc.document_type === "teachers_registration"
  );
  const australianWorkVisaCertificate = qualificationDocs?.documents?.find(
    (doc: TUploadedDocumentsData) =>
      doc.document_type === "australian_work_visa"
  );
  const permanentResidencyVisaCertificate = qualificationDocs?.documents?.find(
    (doc: TUploadedDocumentsData) =>
      doc.document_type === "permanent_residency_visa"
  );
  const courseProgressionLetterCertificate = qualificationDocs?.documents?.find(
    (doc: TUploadedDocumentsData) =>
      doc.document_type === "course_progression_letter"
  );



  const wcc_valid_until_str = workingWithChildrenCheckCertificate?.validity_date;
  const first_aid_valid_until_str = firstAidCertificate?.issued_date;
  const mandatory_reporting_valid_until_str = mandatoryReportingTrainingCertificate?.issued_date
  const teachers_registration_valid_until_str = teachersRegCertificate?.validity_date;
  const post_graduate_diploma_valid_until_str = postGraduateCertificate?.completion_date;
  const bachelor_or_degree_valid_until_str = bachelorOrDegreeCertificate?.completion_date
  const certificate_iii_valid_until_str = certificateIIICertificate?.completion_date;
  const diploma_valid_until_str = diplomaCertificate?.completion_date;
  const food_handler_certificate_valid_until_str = foodHandlerCertificate?.issued_date;
  const cooking_qualification_valid_until_str = cookingQualificationCertificate?.completion_date;
  const australian_work_visa_valid_until_str = australianWorkVisaCertificate?.validity_date;


  /** Set the values for country if the user chosen others in studies completed location */
  const changeHandler = (value: any) => {
    setSelectedCountry(value.value);
  };

  /** Set the value for qualification and reset the values if user changed the option */
  const handleQualification = (value: string) => {
    handleQualificationsReset(value);
    setFieldValue("qualification", value);
  };

  /** Set the value for current study meets */
  const handleCurrentStudyMeets = async (value: string) => {
    if (
      value === "no_course_letter" &&
      courseProgressionLetterCertificate?.file_path
    ) {
      await handleDeleteCourseProgressionLetter();
    }
    setSelectedEducatorPositions([]);
    setFieldValue("educator_teacher_positions", []);
    setFieldValue("currently_studying_option", value);
  };

  /** Set the value for studies completed location and reset the values if user changed the option*/
  const handleStudiesCompletedLocation = (value: string) => {
    handleStudiesCompleteLocationReset();
    setFieldValue("studies_completed_location", value);
  };

  /** Set the value for diploma certificate level */
  const handleDiplomaCertificateLevel = (value: string) => {
    setFieldValue("diploma_jobs_at_certificate_level", value);
  };

  const handleDeleteFHCertificate = async () => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(
      deleteCandidateDocumentThunk({
        document_type: "food_handler_certificate",
        candidate_uuid: isSuperAdmin ? reviewCandidateData?.uuid : "",
      })
    );
    dispatch(hideTransparentPreloaderAction());
  };

  const handleDeleteCookingQualificationCertificate = async () => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(
      deleteCandidateDocumentThunk({
        document_type: "cooking_qualification",
        candidate_uuid: isSuperAdmin ? reviewCandidateData?.uuid : "",
      })
    );
    dispatch(hideTransparentPreloaderAction());
  };

  const handleDeleteCourseProgressionLetter = async () => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(
      deleteCandidateDocumentThunk({
        document_type: "course_progression_letter",
        candidate_uuid: isSuperAdmin ? reviewCandidateData?.uuid : "",
      })
    );
    dispatch(hideTransparentPreloaderAction());
  };

  /** Handle educator/teacher position selection changes */
  const handleEducatorPositionsChange = (positions: string[]) => {
    setSelectedEducatorPositions(positions);
  };

  /** Handle alternative position selection changes, including Chef/Cook document cleanup */
  const handleAlternativePositionsChange = async (positions: string[]) => {
    setSelectedAlternativePosition(positions);

    if (
      !positions.includes(ALTERNATIVE_POSITIONS.CHEF_COOK) &&
      foodHandlerCertificate?.file_path
    ) {
      handleDeleteFHCertificate();
    }

    if (
      !positions.includes(ALTERNATIVE_POSITIONS.CHEF_COOK) &&
      cookingQualificationCertificate?.file_path
    ) {
      handleDeleteCookingQualificationCertificate();
    }
  };

  /** Update the values in the array for alternative_positions and qualification levels based on the type */
  const handleCheckBox = (checked: boolean, value: string, type: string) => {
    if (type === "AlternativePositions") {
      const updatedPositions = checked
        ? [...selectedAlternativePosition, value]
        : selectedAlternativePosition.filter((item) => item !== value);
      handleAlternativePositionsChange(updatedPositions);
    } else if (type === "QualificationLevels") {
      setSelectedQualificationLevels(
        checked
          ? [...selectedQualificationLevels, value]
          : selectedQualificationLevels.filter((item) => item !== value)
      );
    }
  };

  /** Update the value for all document uploads dynamically */
  const handleFileSelect = (file: TFileUploadsResponse, fileName: string) => {
    setFieldValue(file.data.document_type + "_doc", file.data.document);
    setFieldValue(file.data.document_type + "_doc_name", fileName);
  };

  /** Submit the form for qualifications */
  const { handleSubmit, values, errors, setFieldValue } = useFormik({
    validationSchema: createValidationSchema("qualifications"),
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: {
      qualification: qualifications?.qualification
        ? qualifications?.qualification
        : education
        ? education
        : "",
      currently_studying_option: qualifications?.currently_studying_option
        ? qualifications?.currently_studying_option
        : currently_equivalent
        ? currently_equivalent
        : "",
      studies_completed_location: qualifications?.studies_completed_location
        ? qualifications?.studies_completed_location
        : educated_location
        ? educated_location
        : "",
      other_study_selected_country: selectedCountry
        ? selectedCountry
        : other_country
        ? other_country
        : "",
      acecqa_approved: isQualificationApproved
        ? isQualificationApproved
        : acecqa_app
        ? acecqa_app
        : false,
      qualification_accept_options: selectedQualificationLevels
        ? selectedQualificationLevels
        : [],
      educator_teacher_positions: selectedEducatorPositions
        ? selectedEducatorPositions
        : [],

      diploma_jobs_at_certificate_level:
        qualifications?.diploma_jobs_at_certificate_level
          ? qualifications?.diploma_jobs_at_certificate_level
          : certificate_diploma_jobs
          ? certificate_diploma_jobs
          : "",
      alternative_positions: selectedAlternativePosition
        ? selectedAlternativePosition
        : [],
    },
    onSubmit: async (values: TQualificationsFormValues) => {
      dispatch(setMissingDocsCountAction(missingDocs.length));
      const payload = { ...values };
      // Only set when a Super‑Admin edits a candidate profile; required by the API to identify the candidate.
      if (
        isSuperAdmin &&
        userpersonaldetails &&
        "uuid" in userpersonaldetails
      ) {
        payload.candidate_uuid = userpersonaldetails.uuid;
        payload.isSuperAdmin = isSuperAdmin;
      }

      if(isImpersonating && impersonationData?.candidate?.uuid) {
        payload.candidate_uuid = impersonationData?.candidate?.uuid;
      }
      dispatch(showTransparentPreloaderAction());
      try {
        const storedRequestId = localStorage.getItem("request_id");
        const storedRequestFrom = localStorage.getItem("request_from");
        const result = await dispatch(qualificationsThunk(payload));
        if (isFulfilled(result)) {
          if (isSuperAdmin) {
            // storedRequestId === null &&
            //   dispatch(showNotificationAction("Updated"));
            /** read the request_id and request_from that was persisted in localStorage */
              if (storedRequestId && storedRequestFrom) {
                const editResponse = await dispatch(
                  profileEditRequestStatusThunk({
                    request_id: storedRequestId,
                    request_from: storedRequestFrom,
                  })
                );
              }

              // if (isFulfilled(editResponse)) {
                const candidateResponse = await dispatch(
                  getCandidateData({ candidate_id: userpersonaldetails?.uuid })
                );
                if (isFulfilled(candidateResponse)) {
                  dispatch(showNotificationAction("Updated"));
                }
              // }

              CandidateStorage.save(userpersonaldetails?.profile?.id ?? 0);
            
          } else {
            if (checkPath) {
              navigate("/candidate/my-profile/job-preferences");
            } else {
              navigate(AUTH_ROUTE.JOB_PREFERENCES);
            }
          }
        }
      } catch (error) {
        throw new Error(
          "Could not connect to services at the moment. Please try again. Thanks."
        );
      }
      dispatch(hideTransparentPreloaderAction());
    },
  });



  useEffect(() => {
    setMissingDocs(
      computeMissingDocs(values, workingRights, qualificationDocs?.documents)
    );
  }, [
    checkPath,
    stepCompletion,
    values.qualification,
    values.currently_studying_option,
    values.acecqa_approved,
    values.alternative_positions,
    (!isSuperAdmin && qualificationsDocs), 
    (isSuperAdmin && reviewDocuments)
  ]);

  /** Set the value for ACECQA qualification approved or not, if the user chosen New-Zealand/Others in studies completed location */
  const toggleIsQualificationApproved = async () => {
    setIsQualificationApproved(!isQualificationApproved);
    if (
      !!values.acecqa_approved === true &&
      acecqaCertificate !== undefined &&
      acecqaCertificate?.file_path !== ""
    ) {
      dispatch(showTransparentPreloaderAction());
      await dispatch(
        deleteCandidateDocumentThunk({
          document_type: "ACECQA_assessment",
          candidate_uuid: isSuperAdmin ? reviewCandidateData?.uuid : "",
        })
      );
      dispatch(hideTransparentPreloaderAction());
    }
  };

  /** Check and set the values in array for alternative_positions and qualification levels */
  useEffect(() => {
    if (selectedQualificationLevels) {
      setFieldValue(
        "qualification_accept_options",
        selectedQualificationLevels
      );
    }

    if (selectedAlternativePosition) {
      setFieldValue("alternative_positions", selectedAlternativePosition);
    }

    setFieldValue("educator_teacher_positions", selectedEducatorPositions);

    setFieldValue("other_study_selected_country", selectedCountry);

    setFieldValue("acecqa_approved", isQualificationApproved);
  }, [
    setFieldValue,
    selectedQualificationLevels,
    selectedAlternativePosition,
    selectedEducatorPositions,
    selectedCountry,
    isQualificationApproved,
  ]);

  
  /** Filtering the value based on the degree chosen in the qualification for pg/bachelor */
  const filteredQualificationLevels = useMemo(() => {
    const otherQualificationLevels = [
      OTHER_QUALIFICATIONS_LEVEL.BACHELOR_OR_DEGREE,
      OTHER_QUALIFICATIONS_LEVEL.DIPLOMA,
      OTHER_QUALIFICATIONS_LEVEL.CERTIFICATE_III,
    ];

    const qualificationLevelsListVals =
      values.qualification === "bachelor_or_degree"
        ? otherQualificationLevels.filter(
            (qualificationLevel: string) =>
              qualificationLevel !== "Bachelor or Degree"
          )
        : otherQualificationLevels;
    return qualificationLevelsListVals;
  }, [values.qualification]);

  /** Reset the values in the user chosen other value in qualification */
  const handleQualificationsReset = async (qualification: string) => {
    const otherThanPgUgArr = [
      "diploma",
      "certificate_iii",
      "currently_studying",
      "no_qualifications",
    ];
    setFieldValue("studies_completed_location", "");
    setFieldValue("currently_studying_option", "");
    setFieldValue(
      "qualification_accept_options",
      setSelectedQualificationLevels([])
    );
    setFieldValue("diploma_jobs_at_certificate_level", "");
    setSelectedEducatorPositions([]);
    setFieldValue("educator_teacher_positions", []);
    setSelectedAlternativePosition([]);
    setFieldValue("alternative_positions", []);
    handleStudiesCompleteLocationReset();
    if (
      (values.qualification === "post_graduate_diploma" &&
        postGraduateCertificate !== undefined &&
        postGraduateCertificate?.file_path !== "") ||
      (values.qualification === "bachelor_or_degree" &&
        bachelorOrDegreeCertificate !== undefined &&
        bachelorOrDegreeCertificate?.file_path !== "") ||
      (values.qualification === "diploma" &&
        diplomaCertificate !== undefined &&
        diplomaCertificate?.file_path !== "") ||
      (values.qualification === "certificate_iii" &&
        certificateIIICertificate !== undefined &&
        certificateIIICertificate?.file_path !== "")
    ) {
      dispatch(showTransparentPreloaderAction());
      await dispatch(
        deleteCandidateDocumentThunk({
          document_type: values.qualification,
          candidate_uuid: isSuperAdmin ? reviewCandidateData?.uuid : "",
        })
      );
      dispatch(hideTransparentPreloaderAction());
    }
    if (
      values.qualification === "currently_studying" &&
      courseProgressionLetterCertificate !== undefined &&
      courseProgressionLetterCertificate?.file_path !== ""
    ) {
      dispatch(showTransparentPreloaderAction());
      await dispatch(
        deleteCandidateDocumentThunk({
          document_type: "course_progression_letter",
          candidate_uuid: isSuperAdmin ? reviewCandidateData?.uuid : "",
        })
      );
      dispatch(hideTransparentPreloaderAction());
    }
    if (
      otherThanPgUgArr.includes(qualification) &&
      teachersRegCertificate !== undefined &&
      teachersRegCertificate?.file_path !== ""
    ) {
      dispatch(showTransparentPreloaderAction());
      await dispatch(
        deleteCandidateDocumentThunk({
          document_type: "teachers_registration",
          candidate_uuid: isSuperAdmin ? reviewCandidateData?.uuid : "",
        })
      );
      dispatch(hideTransparentPreloaderAction());
    }
  };

  /** Reset the values in the user chosen different value in studies completed location */
  const handleStudiesCompleteLocationReset = async () => {
    setFieldValue("other_study_selected_country", setSelectedCountry(""));
    setFieldValue("other_study_selected_country", "");
    setFieldValue("acecqa_approved", setIsQualificationApproved(false));
    setFieldValue("acecqa_approved", false);
    if (
      !!values.acecqa_approved === true &&
      acecqaCertificate !== undefined &&
      acecqaCertificate?.file_path !== ""
    ) {
      dispatch(showTransparentPreloaderAction());
      await dispatch(
        deleteCandidateDocumentThunk({
          document_type: "ACECQA_assessment",
          candidate_uuid: isSuperAdmin ? reviewCandidateData?.uuid : "",
        })
      );
      dispatch(hideTransparentPreloaderAction());
    }
  };

  const isBtnDisabled =
    (workingWithChildrenCheckCertificate && !wcc_valid_until_str) ||
    (firstAidCertificate && !first_aid_valid_until_str) ||
    (mandatoryReportingTrainingCertificate && !mandatory_reporting_valid_until_str) ||
    (teachersRegCertificate && !teachers_registration_valid_until_str) ||
    (postGraduateCertificate && !post_graduate_diploma_valid_until_str) ||
    (bachelorOrDegreeCertificate && !bachelor_or_degree_valid_until_str) ||
    (certificateIIICertificate && !certificate_iii_valid_until_str) ||
    (diplomaCertificate && !diploma_valid_until_str) ||
    (foodHandlerCertificate && !food_handler_certificate_valid_until_str) ||
    (cookingQualificationCertificate && !cooking_qualification_valid_until_str) ||
    (australianWorkVisaCertificate && !australian_work_visa_valid_until_str);
  
  /** Check all the values got updated for enabling the next button */
  useEffect(() => {
    const qualificationsArr = [
      "post_graduate_diploma",
      "bachelor_or_degree",
      "diploma",
      "certificate_iii",
    ];
    
    const check1 = values.qualification !== "";
    const check2 = qualificationsArr.includes(values.qualification)
      ? values.studies_completed_location !== ""
      : values.studies_completed_location === "";
    const check3 = (values.qualification !== "" &&
      values.studies_completed_location === "other")
      ? values.other_study_selected_country !== ""
      : values.other_study_selected_country === "";
    const check4 = (values.qualification === "currently_studying" ||
      values.qualification === "no_qualifications")
      ? values.studies_completed_location === ""
      : values.studies_completed_location !== "";
    // const check5 = values.qualification === "diploma"
    //   ? values.diploma_jobs_at_certificate_level !== ""
    //   : values.diploma_jobs_at_certificate_level === "";
    const check6 = values.qualification !== ""
      ? (selectedEducatorPositions.length > 0 || selectedAlternativePosition.length > 0)
      : true;
    const checkStudyingOption = values.qualification !== "currently_studying" ||
      ["diploma_equivalent", "certificate_iii_equivalent"].includes(values.currently_studying_option);
    const check7 = !isBtnDisabled;
    const check8 = checkPath ? missingDocs.length === 0 : true;
    
    if (check1 && check2 && check3 && check4  && check6 && checkStudyingOption && check7 && check8) { 
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }

    sessionStorage.setItem("qualification", values.qualification);
    sessionStorage.setItem(
      "studies_completed_location",
      values.studies_completed_location
    );
    sessionStorage.setItem(
      "currently_studying_option",
      values.currently_studying_option
    );
    sessionStorage.setItem(
      "other_study_selected_country",
      values.other_study_selected_country ?? ""
    );
    sessionStorage.setItem(
      "acecqa_approved",
      (isQualificationApproved || false).toString()
    );
    sessionStorage.setItem(
      "qualification_accept_options",
      selectedQualificationLevels
        ? JSON.stringify(selectedQualificationLevels)
        : "[]"
    );
    sessionStorage.setItem(
      "diploma_jobs_at_certificate_level",
      values.diploma_jobs_at_certificate_level
    );
    sessionStorage.setItem(
      "alternative_positions",
      selectedAlternativePosition
        ? JSON.stringify(selectedAlternativePosition)
        : "[]"
    );
    sessionStorage.setItem(
      "educator_teacher_positions",
      selectedEducatorPositions
        ? JSON.stringify(selectedEducatorPositions)
        : "[]"
    );
  }, [
    values,
    selectedAlternativePosition,
    selectedQualificationLevels,
    selectedEducatorPositions,
    isQualificationApproved,
    isBtnDisabled,
    missingDocs,
    checkPath,
  ]);

  useEffect(() => {
    if (location.pathname.includes("candidate")) {
      setCheckPath(true);
    }
  }, [location]);

  const getLink = useMemo(() => {
    const input =
      userpersonaldetails !== null
        ? userpersonaldetails?.state?.trim().toUpperCase()
        : "";
    const abbr = STATE_ALIAS[input as string] || input;
    return (
      STATE_LINKS[abbr as string] ||
      "https://www.vic.gov.au/become-early-childhood-teacher-educator"
    );
  }, [userpersonaldetails]);

  // Get the review status for the current profile
  const reviewStatus = profileData?.review_status;

  /**
   * Determines if a field should be disabled based on status and user role
   * - Super Admin during impersonation: Can ALWAYS edit (any status)
   * - Candidate with "approved" status: CANNOT edit (completed and approved)
   * - Candidate with "waiting" status: CAN edit (submitted for review)
   * - Candidate with null status: CAN edit (not yet submitted)
   */
  const shouldFieldBeDisabled = () => {
    // Super Admin during impersonation can always edit
    if (isImpersonating) {
      return false;
    }
    
    // Super Admin not impersonating (direct edit)
    if (isSuperAdmin) {
      return false;
    }
    
    // For candidates: only disable if status is "approved" (completed and approved)
    // Allow editing during "waiting" (submitted for review) and null (not submitted)
    return reviewStatus === 'approved';
  };

  const checkStageSubmittedOrNot = (stageNumber: number | null) => {
    return shouldFieldBeDisabled();
  };

  const handleDocDate = (type: string, val: Date | null) => {
    if (!val) return;

    const year = val.getFullYear();
    const month = String(val.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
    const day = String(val.getDate()).padStart(2, "0");

    const dateOnly = `${year}-${month}-${day}`; // Local date in YYYY-MM-DD
    dispatch(updateDocumentDateThunk({ document_type: type, date: dateOnly, candidate_uuid: userpersonaldetails?.uuid, isAdmin: isSuperAdmin ? true : false }));
  };

  const isOneMonthBefore = (issueDateStr: string): boolean => {
    if (!issueDateStr) return false;

    const issueDate = new Date(issueDateStr);
    if (isNaN(issueDate.getTime())) return false;

    const today = new Date();
    const oneMonthBefore = new Date(issueDate);
    oneMonthBefore.setMonth(issueDate.getMonth() - 1);

    // Return true only if today is within the 1-month window before issueDate
    return (today >= oneMonthBefore && today <= issueDate) || today > issueDate;
  }

  const checkWCCGoingToExpire = useMemo(() => {
    let checkBefreDate: boolean = false;
    checkBefreDate = isOneMonthBefore(wcc_valid_until_str ?? "");

    return checkBefreDate;
  }, [wcc_valid_until_str]);

  const checkTRGoingToExpire = useMemo(() => {
    let checkBefreDate: boolean = false;
    checkBefreDate = isOneMonthBefore(
      teachers_registration_valid_until_str ?? ""
    );

    return checkBefreDate;
  }, [teachers_registration_valid_until_str])
  
  useEffect(() => {
    const wcc_valid_until =
      wcc_valid_until_str && wcc_valid_until_str !== ""
        ? new Date(wcc_valid_until_str)
        : null;

    const first_aid_valid_until =
      first_aid_valid_until_str && first_aid_valid_until_str !== ""
        ? new Date(first_aid_valid_until_str)
        : null;


    const mandatory_reporting_valid_until =
      mandatory_reporting_valid_until_str &&
      mandatory_reporting_valid_until_str !== ""
        ? new Date(mandatory_reporting_valid_until_str)
        : null;

    const teachers_registration_valid_until =
      teachers_registration_valid_until_str &&
      teachers_registration_valid_until_str !== ""
        ? new Date(teachers_registration_valid_until_str)
        : null;

    const post_graduate_diploma_completion_date =
      post_graduate_diploma_valid_until_str &&
      post_graduate_diploma_valid_until_str !== ""
        ? new Date(post_graduate_diploma_valid_until_str)
        : null;


    const bachelor_or_degree_completion_date =
      bachelor_or_degree_valid_until_str &&
      bachelor_or_degree_valid_until_str !== ""
        ? new Date(bachelor_or_degree_valid_until_str)
        : null;

    const certificate_iii_completion_date =
      certificate_iii_valid_until_str && certificate_iii_valid_until_str !== ""
        ? new Date(certificate_iii_valid_until_str)
        : null;

    const diploma_completion_date =
      diploma_valid_until_str && diploma_valid_until_str !== ""
        ? new Date(diploma_valid_until_str)
        : null;

    const food_handler_certificate_valid_until =
      food_handler_certificate_valid_until_str &&
      food_handler_certificate_valid_until_str !== ""
        ? new Date(food_handler_certificate_valid_until_str)
        : null;

      const cooking_qualification_valid_until =
      cooking_qualification_valid_until_str &&
      cooking_qualification_valid_until_str !== ""
        ? new Date(cooking_qualification_valid_until_str)
        : null;
        
    if (!workingWithChildrenCheckCertificate) {
      setWccValidUntil(null);
    } else {
      setWccValidUntil(wcc_valid_until);
    }

    if (!mandatoryReportingTrainingCertificate) {
      setMrtValidUntil(null);
    } else {
      setMrtValidUntil(mandatory_reporting_valid_until);
    }

    if (!firstAidCertificate) {
      setFaValidUntil(null);
    } else {
      setFaValidUntil(first_aid_valid_until);
    }

    if (!teachersRegCertificate) {
      setTrValidUntil(null);
    } else {
      setTrValidUntil(teachers_registration_valid_until);
    }

    if (!postGraduateCertificate) {
      setPgValidUntil(null);
    } else {
      setPgValidUntil(post_graduate_diploma_completion_date);
    }

    if (!bachelorOrDegreeCertificate) {
      setUgValidUntil(null);
    } else {
      setUgValidUntil(bachelor_or_degree_completion_date);
    }

    if (!certificateIIICertificate) {
      setCiiiValidUntil(null);
    } else {
      setCiiiValidUntil(certificate_iii_completion_date);
    }

    if (!diplomaCertificate) {
      setDiplValidUntil(null);
    } else {
      setDiplValidUntil(diploma_completion_date);
    }

    if (!foodHandlerCertificate) {
      setFhcValidUntil(null);
    } else {
      setFhcValidUntil(food_handler_certificate_valid_until);
    }

    if (!cookingQualificationCertificate) {
      setCqValidUntil(null);
    } else {
      setCqValidUntil(cooking_qualification_valid_until);
    }

    const australian_work_visa_valid_until =
      australian_work_visa_valid_until_str &&
      australian_work_visa_valid_until_str !== ""
        ? new Date(australian_work_visa_valid_until_str)
        : null;

    

    if (!australianWorkVisaCertificate) {
      setAwvValidUntil(null);
    } else {
      setAwvValidUntil(australian_work_visa_valid_until);
    }

    
  }, [
    workingWithChildrenCheckCertificate, 
    mandatoryReportingTrainingCertificate,
    firstAidCertificate,
    teachersRegCertificate,
    postGraduateCertificate,
    bachelorOrDegreeCertificate,
    certificateIIICertificate,
    diplomaCertificate,
    foodHandlerCertificate,
    australianWorkVisaCertificate,
    permanentResidencyVisaCertificate,
    wcc_valid_until_str,
    mandatory_reporting_valid_until_str,
    first_aid_valid_until_str,
    teachers_registration_valid_until_str,
    post_graduate_diploma_valid_until_str,
    bachelor_or_degree_valid_until_str,
    certificate_iii_valid_until_str,
    diploma_valid_until_str,
    food_handler_certificate_valid_until_str,
    cooking_qualification_valid_until_str,
    australian_work_visa_valid_until_str
  ]);

  return (
    
      <div className="d-flex flex-column gap-20 max-w-650px">
        <div className="d-flex gap-20 align-items-center justify-content-between">
          <div className="fs-30 fw-400 text-black">Qualifications</div>
          {isSuperAdmin && (
            <CustomButton
              variant={"primary"}
              type="button"
              className="btn-theme bg-teal text-black"
              onClick={handleSubmit}
              disabled={isDisabled}
            >
              <RoundedTick color={isDisabled ? "#757575" : "black"}/>
              Save Changes
            </CustomButton>
          )}
        </div>
        <div className="d-flex flex-column gap-20">
          <div className="d-flex flex-column gap-10 theme-check">
            <div className="fs-16 fw-400 text-black">
              Which qualification do you have <strong>in Early Childhood Education</strong>?
            </div>
             <RadioInput
              checked={values.qualification === "certificate_iii"}
              label={EARLY_CHILDHOOD_EDUCATION.CERTIFICATE_III}
              onChange={handleQualification}
              value={"certificate_iii"}
              name="qualification"
              disabled={checkStageSubmittedOrNot(1)}
              themeCheck={true}
            />
             <RadioInput
              checked={values.qualification === "diploma"}
              label={EARLY_CHILDHOOD_EDUCATION.DIPLOMA}
              onChange={handleQualification}
              value={"diploma"}
              name="qualification"
              disabled={checkStageSubmittedOrNot(1)}
              themeCheck={true}
            />
             <RadioInput
              checked={values.qualification === "bachelor_or_degree"}
              label={EARLY_CHILDHOOD_EDUCATION.BACHELOR_OR_DEGREE}
              onChange={handleQualification}
              value={"bachelor_or_degree"}
              name="qualification"
              disabled={checkStageSubmittedOrNot(1)}
              themeCheck={true}
            />
            <RadioInput
              checked={values.qualification === "post_graduate_diploma"}
              label={EARLY_CHILDHOOD_EDUCATION.POST_GRADUATE_DIPLOMA}
              onChange={handleQualification}
              value={"post_graduate_diploma"}
              name="qualification"
              disabled={checkStageSubmittedOrNot(1)}
              themeCheck={true}
            />
            <RadioInput
              checked={values.qualification === "currently_studying"}
              label={EARLY_CHILDHOOD_EDUCATION.I_AM_CURRENTLY_STUDYING}
              onChange={handleQualification}
              value={"currently_studying"}
              name="qualification"
              disabled={checkStageSubmittedOrNot(1)}
              themeCheck={true}
            />
            {values.qualification === "currently_studying" && (
              <div className="d-flex flex-column gap-10 pl-50px theme-check">
                <RadioInput
                  checked={
                    values.currently_studying_option === "diploma_equivalent"
                  }
                  label={
                    "I can provide a course progression letter confirming I meet the equivalent requirements of a Diploma level"
                  }
                  onChange={handleCurrentStudyMeets}
                  value={"diploma_equivalent"}
                  name="currently_studying_option"
                  disabled={checkStageSubmittedOrNot(1)}
                  themeCheck={true}
                />
                <RadioInput
                  checked={
                    values.currently_studying_option ===
                    "certificate_iii_equivalent"
                  }
                  label={
                    "I can provide a course progression letter confirming I meet the equivalent requirements of a Certificate III level"
                  }
                  onChange={handleCurrentStudyMeets}
                  value={"certificate_iii_equivalent"}
                  name="currently_studying_option"
                  disabled={checkStageSubmittedOrNot(1)}
                  themeCheck={true}
                />               
                {errors && (
                  <ErrorMessage
                    message={errors.currently_studying_option as string}
                  />
                )}
              </div>
            )}
            <RadioInput
              checked={values.qualification === "no_qualifications"}
              label={EARLY_CHILDHOOD_EDUCATION.I_HAVE_NO_QUALIFICATION}
              onChange={handleQualification}
              value={"no_qualifications"}
              name="qualification"
              disabled={checkStageSubmittedOrNot(1)}
              themeCheck={true}
            />
            {errors && (
              <ErrorMessage message={errors.qualification as string} />
            )}
          </div>
          {values.qualification === "currently_studying" &&
            values.currently_studying_option === "no_course_letter" && (
              <>
                <div className="bg-teal-light px-15px py-10px radius-8px d-flex gap-6">
                  <Warning />
                  <div className="fs-13 fw-400 text-black lh-sm">
                    To be eligible to register as an Educator you will need to
                    have completed your studies in Early Childhood Education.
                    You can continue creating your profile as an Alternative
                    Position below, and edit your profile settings after
                    completing your studies.
                  </div>
                </div>
              </>
            )}
          {values.qualification === "no_qualifications" && (
            <>
              <div className="bg-teal-light px-15px py-10px radius-8px d-flex gap-6">
                <Warning />
                <div className="fs-13 fw-400 text-black lh-sm">
                  To be eligible to register as an Educator you will need
                  qualifications.{" "}
                  <Link
                    className="fw-700 text-black"
                    to={getLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Find out more about becoming a qualified Early Childhood
                    Teacher or Educator.
                  </Link>{" "}
                  You can continue creating your profile as an Alternative
                  Position below, and edit your profile settings after
                  completing your studies.
                </div>
              </div>
            </>
          )}
          {values.qualification !== "" &&
            values.qualification !== "currently_studying" &&
            values.qualification !== "no_qualifications" && (
              <>
                <div className="w-100 h-1px"></div>
                <div className="d-flex flex-column gap-10 theme-check">
                  <div className="fs-16 fw-400 text-black">
                    Where did you complete your studies in Early Childhood Education?
                  </div>
                  <div className="theme-check">
                    <RadioInput
                      checked={
                        values.studies_completed_location === "australia"
                      }
                      label={COMPLETE_YOUR_STUDIES.AUSTRALIA}
                      onChange={handleStudiesCompletedLocation}
                      value={"australia"}
                      name="studies_completed_location"
                      disabled={checkStageSubmittedOrNot(1)}
                      themeCheck={true}
                    />
                  </div>
                  <div className="theme-check">
                    <RadioInput
                      checked={
                        values.studies_completed_location === "new_zealand"
                      }
                      label={COMPLETE_YOUR_STUDIES.NEW_ZEALAND}
                      onChange={handleStudiesCompletedLocation}
                      value={"new_zealand"}
                      name="studies_completed_location"
                      disabled={checkStageSubmittedOrNot(1)}
                      themeCheck={true}
                    />
                  </div>
                  {values.studies_completed_location === "new_zealand" && (
                    <div className="pl-50px theme-check">
                      <CheckboxInput
                        checked={
                          values.acecqa_approved
                            ? values.acecqa_approved
                            : isQualificationApproved
                        }
                        label="Please tick to confirm your qualification is ACECQA approved."
                        id={"acecqa_approved"}
                        onChange={toggleIsQualificationApproved}
                        className="theme-check-input"
                        themeCheck={true}
                        disabled={checkStageSubmittedOrNot(1)}
                      />
                    </div>
                  )}
                  <div className="theme-check">
                    <RadioInput
                      checked={values.studies_completed_location === "other"}
                      label={COMPLETE_YOUR_STUDIES.OTHER}
                      onChange={handleStudiesCompletedLocation}
                      value={"other"}
                      name="studies_completed_location"
                      themeCheck={true}
                      disabled={checkStageSubmittedOrNot(1)}
                    />
                  </div>
                  {values.studies_completed_location === "other" && (
                    <div className="d-flex flex-column gap-10 other_country_select_container pl-50px">
                      <div className="max-w-295px">
                        <CustomSelect
                          options={options}
                          placeholder="Select..."
                          onSelect={changeHandler}
                          enableFilter={true}
                          defaultSelected={options.find(
                            (item) =>
                              item.value === values.other_study_selected_country
                          )}
                          disabled={checkStageSubmittedOrNot(1)}
                        />
                      </div>
                      {/* <SelectInput
                        className="max-w-295px form-select"
                        options={options}
                        onChange={changeHandler}
                        value={selectedCountry}
                      /> */}
                      {errors && (
                        <ErrorMessage
                          message={
                            errors.other_study_selected_country as string
                          }
                        />
                      )}
                      <div className="theme-check">
                        <CheckboxInput
                          checked={isQualificationApproved}
                          label="Please tick to confirm your qualification is ACECQA approved."
                          onChange={toggleIsQualificationApproved}
                          disabled={checkStageSubmittedOrNot(1)}
                          className="theme-check-input"
                          themeCheck={true}
                          id={uuidv4()}
                        />
                        {errors && (
                          <ErrorMessage
                            message={errors.acecqa_approved as string}
                          />
                        )}
                      </div>
                    </div>
                  )}
                  {errors && (
                    <ErrorMessage
                      message={errors.studies_completed_location as string}
                    />
                  )}
                </div>
                {(values.studies_completed_location === "new_zealand" ||
                  values.studies_completed_location === "other") &&
                  !isQualificationApproved && (                    
                      <div className="bg-teal-light px-15px py-10px radius-8px d-flex gap-6">
                        <Warning />
                        <div className="fs-13 fw-400 text-black lh-sm">
                          <Link
                            className="fw-700 text-black"
                            to="https://www.acecqa.gov.au/qualifications/assessment/apply/early-childhood"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Find out more about getting your Early Childhood
                            qualifications assessed by ACECQA.
                          </Link>
                          You can continue creating your profile as an
                          Alternative Position below, and edit your profile
                          settings after you receive approval.
                        </div>
                      </div>                   
                  )}
              </>
            )}
          
          
          <div className="w-100 h-1px"></div>
          {
            values.qualification !== "" && (
              <PositionTypes
                qualification={values.qualification}
                currentlyStudyingOption={values.currently_studying_option}
                selectedEducatorPositions={selectedEducatorPositions}
                selectedAlternativePositions={selectedAlternativePosition}
                onEducatorPositionsChange={handleEducatorPositionsChange}
                onAlternativePositionsChange={handleAlternativePositionsChange}
                disabled={checkStageSubmittedOrNot(1)}
                alternativePositionError={errors.alternative_positions as string}
              />
            )
          }
          
          
          <div className="fs-22 fw-400 text-black">Key Documents</div>
          <div className="fs-16 fw-400 text-black">
            If your key document files are not accessible right now and you would prefer to upload <br />
            them later, you can continue onboarding then submit Stage 1 after.
          </div>

          {
            workingRights === "australian_visa_holder" && (
            <>
            <div className="d-flex flex-column gap-10 awv_upload_container">
              <div className="fs-16 fw-700 text-black">
                Australian Work Visa
              </div>
              <DocumentUpload
                onFileSelect={handleFileSelect}
                type={"australian_work_visa"}
                candidate_uuid={
                  isSuperAdmin ? userpersonaldetails?.uuid : null
                }
                disabled={checkStageSubmittedOrNot(1)}
              />
              <div className="d-flex flex-column flex-md-row gap-10 align-items-md-center">
                <div className="fs-13 fw-400 text-grey-dark text-nowrap">
                  Valid until:
                </div>
                <div className="min-w-200px">
                  <DatePicker
                    selected={
                      australianWorkVisaCertificate
                        ? awvValidUntil
                        : null
                    }
                    onChange={(date: Date | null) => {
                      setAwvValidUntil(date);
                      handleDocDate("australian_work_visa", date);
                    }}
                    customInput={
                      <CustomDateInput
                        disabled={checkStageSubmittedOrNot(1)}
                        placeholder="Select a date..."
                      />
                    }
                    dateFormat="dd MMM yyyy"
                    minDate={new Date()}
                    disabled={
                      !australianWorkVisaCertificate || checkStageSubmittedOrNot(1)
                    }
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={15}
                  />
                </div>
                </div>
                {checkPath && missingDocs.includes("australian_work_visa") ? (
                  <ErrorMessage
                    className="align-items-center"
                    message="Australian Work Visa is required"
                  />
                ) : (
                  ""
                )}
              
            </div>
            {(australianWorkVisaCertificate && !australian_work_visa_valid_until_str) && (
              <ErrorMessage className="align-items-center gap-3 lh-1px" message="Please select the date this document is valid until" />
            )}
            <div className="bg-off-white min-h-1px"></div>
            </>
          )}

          {
            workingRights === "australian_permanent_resident" && (
              <>
                <div className="d-flex flex-column gap-10 prv_upload_container">
                  <div className="fs-16 fw-700 text-black">
                    Permanent Residency Visa
                  </div>
                  <DocumentUpload
                    onFileSelect={handleFileSelect}
                    type={"permanent_residency_visa"}
                    candidate_uuid={
                      isSuperAdmin ? userpersonaldetails?.uuid : null
                    }
                    disabled={checkStageSubmittedOrNot(1)}
                  />
                  <div className="d-flex flex-column flex-md-row gap-10 align-items-md-center">
                    
                  </div>
                  {checkPath && missingDocs.includes("permanent_residency_visa") ? (
                    <ErrorMessage
                      className="align-items-center"
                      message="Permanent Residency Visa is required"
                    />
                  ) : (
                    ""
                  )}
                </div>
                
                <div className="bg-off-white min-h-1px"></div>
              </>
            )
          }
          
          <div className="d-flex flex-column gap-10 wcc_upload_container">
            <div className="fs-16 fw-700 text-black">
              Working With Children Check / Blue Card
            </div>
            <DocumentUpload
              onFileSelect={handleFileSelect}
              type={"working_with_children_check"}
              candidate_uuid={isSuperAdmin ? userpersonaldetails?.uuid : isImpersonating ? impersonationData?.candidate?.uuid : null}
              disabled={checkStageSubmittedOrNot(1)}
            />
            <div className="d-flex flex-column flex-md-row gap-10 align-items-md-center">
              <div className="fs-13 fw-400 text-grey-dark text-nowrap">Valid until:</div>
              <div className="min-w-200px">
                <DatePicker
                  selected={
                    workingWithChildrenCheckCertificate
                      ? wccValidUntil
                      : null // ⛔ Don't show a date if the document is missing
                  }
                  onChange={(date: Date | null) => {
                    setWccValidUntil(date)
                    handleDocDate("working_with_children_check", date);
                  }}
                  customInput={
                    <CustomDateInput
                      disabled={checkStageSubmittedOrNot(1)}
                      placeholder="Select a date..."
                    />
                  }
                  dateFormat="dd MMM yyyy"
                  minDate={new Date()}
                  disabled={
                    !workingWithChildrenCheckCertificate ||
                    checkStageSubmittedOrNot(1)
                  }
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={15}
                />

                
              </div>
              { checkWCCGoingToExpire && 
                <ErrorMessage className="align-items-center gap-3 lh-1" message={ExpiryAlertMsg} />
              }
              
            </div>
          </div>
          {(workingWithChildrenCheckCertificate && !wcc_valid_until_str) && (
            <ErrorMessage className="align-items-center gap-3 lh-1px" message="Please select the date this document is valid until" />
          )}
          {checkPath && missingDocs.includes("working_with_children_check") ? (
                <ErrorMessage
                  className="align-items-center"
                  message="Working With Children Check is required"
                />
              ) : (
                ""
              )}
          <div className="bg-off-white min-h-1px"></div>
          <div className="d-flex flex-column gap-10 fac_upload_container">
            <div className="fs-16 fw-700 text-black">First Aid Certificate {!(
              ["post_graduate_diploma", "bachelor_or_degree", "certificate_iii", "diploma"].includes(values.qualification) ||
              (values.qualification === "currently_studying" &&
                ["diploma_equivalent", "certificate_iii_equivalent"].includes(values.currently_studying_option))
            ) && (
              <span className="fs-13 fw-400 text-grey-medium">(optional)</span>
            )}</div>
            <DocumentUpload
              onFileSelect={handleFileSelect}
              type={"first_aid_certificate"}
              candidate_uuid={isSuperAdmin ? userpersonaldetails?.uuid : isImpersonating ? impersonationData?.candidate?.uuid : null}
              disabled={checkStageSubmittedOrNot(1)}
            />
            <div className="d-flex flex-column flex-md-row gap-10 align-items-md-center">
              <div className="fs-13 fw-400 text-grey-dark text-nowrap">Issue date:</div>
              <div className="min-w-200px">
                <DatePicker
                  selected={
                    firstAidCertificate
                      ? faValidUntil
                      : null
                  }
                  onChange={(date: Date | null) => {
                    setFaValidUntil(date)
                    handleDocDate("first_aid_certificate", date);
                  }}
                  customInput={
                    <CustomDateInput
                      disabled={checkStageSubmittedOrNot(1)}
                      placeholder="Select a date..."
                    />
                  }
                  dateFormat="dd MMM yyyy"
                  minDate={getMinDateByDocumentType("first_aid_certificate")}
                  maxDate={new Date()}
                  disabled={!firstAidCertificate || checkStageSubmittedOrNot(1)}
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={15}
                />
              </div>
              <div className="d-flex flex-row fs-13 fw-400 text-grey-dark gap-3">
                <Warning width={"15"} height={"15"} color={"#464646"} />
                Expires {getValidityMonths('first_aid_certificate')} months after issue date and will need renewing
              </div>
            </div>
          </div>
          {(firstAidCertificate && !first_aid_valid_until_str) && (
            <ErrorMessage className="align-items-center gap-3 lh-1px" message="Please select the date this document was issued" />
          )}
          {checkPath && missingDocs.includes("first_aid_certificate") ? (
            <ErrorMessage
              className="align-items-center"
              message="First Aid Certificate is required"
            />
          ) : (
            ""
          )}
          <div className="bg-off-white min-h-1px"></div>
          <div className="d-flex flex-column gap-10 mrt_upload_container">
            <div className="fs-16 fw-700 text-black">
              Mandatory Reporting Training
            </div>
            <DocumentUpload
              onFileSelect={handleFileSelect}
              type={"mandatory_reporting_training"}
              candidate_uuid={isSuperAdmin ? userpersonaldetails?.uuid : isImpersonating ? impersonationData?.candidate?.uuid : null}
              disabled={checkStageSubmittedOrNot(1)}
            />
            <div className="d-flex flex-column flex-md-row gap-10 align-items-md-center">
              <div className="fs-13 fw-400 text-grey-dark text-nowrap">Issue date:</div>
              <div className="min-w-200px">
                <DatePicker
                  selected={
                    mandatoryReportingTrainingCertificate
                      ? mrtValidUntil
                      : null
                  }
                  onChange={(date: Date | null) => {
                    setMrtValidUntil(date);
                    handleDocDate("mandatory_reporting_training", date);
                  }}
                  customInput={
                    <CustomDateInput
                      disabled={checkStageSubmittedOrNot(1)}
                      placeholder="Select a date..."
                    />
                  }
                  dateFormat="dd MMM yyyy"
                  minDate={getMinDateByDocumentType("mandatory_reporting_training")}
                  maxDate={new Date()}
                  disabled={
                    !mandatoryReportingTrainingCertificate ||
                    checkStageSubmittedOrNot(1)
                  }
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={15}
                />
              </div>
              <div className="d-flex flex-row fs-13 fw-400 text-grey-dark gap-3">
                <Warning width={"15"} height={"15"} color={"#464646"} />
                Expires {getValidityMonths('mandatory_reporting_training')} months after issue date and will need renewing
              </div>
            </div>
          </div>
          {(mandatoryReportingTrainingCertificate && !mandatory_reporting_valid_until_str) && (
            <ErrorMessage className="align-items-center gap-3 lh-1px" message="Please select the date this document was issued" />
          )}
          {checkPath && missingDocs.includes("mandatory_reporting_training") ? (
            <ErrorMessage
              className="align-items-center"
              message="Mandatory Reporting Training is required"
            />
          ) : (
            ""
              )}
          <div className="bg-off-white min-h-1px"></div>
          {(values.qualification === "post_graduate_diploma" ||
            values.qualification === "bachelor_or_degree") && (
            <>
              <div className="d-flex flex-column gap-10 teachers_doc_upload_container">
                <div className="fs-16 fw-700 text-black">
                  Teacher Registration
                </div>
                <DocumentUpload
                  onFileSelect={handleFileSelect}
                  type={"teachers_registration"}
                  candidate_uuid={
                    isSuperAdmin ? userpersonaldetails?.uuid : isImpersonating ? impersonationData?.candidate?.uuid : null
                  }
                  disabled={checkStageSubmittedOrNot(1)}
                />
                <div className="d-flex flex-column flex-md-row gap-10 align-items-md-center">
                  <div className="fs-13 fw-400 text-grey-dark text-nowrap">
                    Valid until:
                  </div>
                  <div className="min-w-200px">
                    <DatePicker
                      selected={
                        teachersRegCertificate ? trValidUntil : null
                      }
                      onChange={(date: Date | null) => {
                        setTrValidUntil(date);
                        handleDocDate("teachers_registration", date);
                      }}
                      customInput={
                        <CustomDateInput
                          disabled={checkStageSubmittedOrNot(1)}
                          placeholder="Select a date..."
                        />
                      }
                      dateFormat="dd MMM yyyy"
                      minDate={new Date()}
                      disabled={
                        !teachersRegCertificate || checkStageSubmittedOrNot(1)
                      }
                      showYearDropdown
                      scrollableYearDropdown
                      yearDropdownItemNumber={15}
                    />
                  </div>
                  { checkTRGoingToExpire && 
                    <ErrorMessage className="align-items-center gap-3 lh-1" message={ExpiryAlertMsg} />
                  }
                </div>
                {(teachersRegCertificate && !teachers_registration_valid_until_str) && (
                  <ErrorMessage className="align-items-center gap-3 lh-1px" message="Please select the date this document is valid until" />
                )}
                {checkPath && missingDocs.includes("teachers_registration") ? (
                  <ErrorMessage
                    className="align-items-center"
                    message="Teacher Registration document is required"
                  />
                ) : (
                  ""
                )}
              </div>
              <div className="bg-off-white min-h-1px"></div>
            </>
          )}
          {values.qualification === "post_graduate_diploma" && (
            <>
              <div className="d-flex flex-column gap-10 pgc_upload_container">
                <div className="fs-16 fw-700 text-black">
                  {EARLY_CHILDHOOD_EDUCATION.POST_GRADUATE_DIPLOMA}
                </div>
                <DocumentUpload
                  onFileSelect={handleFileSelect}
                  type={"post_graduate_diploma"}
                  candidate_uuid={
                    isSuperAdmin ? userpersonaldetails?.uuid : isImpersonating ? impersonationData?.candidate?.uuid : null
                  }
                  disabled={checkStageSubmittedOrNot(1)}
                />
                <div className="d-flex flex-column flex-md-row gap-10 align-items-md-center">
                  <div className="fs-13 fw-400 text-grey-dark text-nowrap">
                    Completion date:
                  </div>
                  <div className="min-w-200px">
                    <DatePicker
                      selected={
                        postGraduateCertificate
                          ? pgValidUntil
                          : null
                      }
                      onChange={(date: Date | null) => {
                        setPgValidUntil(date);
                        handleDocDate("post_graduate_diploma", date);
                      }}
                      customInput={
                        <CustomDateInput
                          disabled={checkStageSubmittedOrNot(1)}
                          placeholder="Select a date..."
                        />
                      }
                      dateFormat="dd MMM yyyy"
                      maxDate={new Date()}
                      disabled={
                        !postGraduateCertificate || checkStageSubmittedOrNot(1)
                      }
                      showYearDropdown
                      scrollableYearDropdown
                      yearDropdownItemNumber={50}
                    />
                  </div>
                </div>
                {(postGraduateCertificate && !post_graduate_diploma_valid_until_str) && (
                  <ErrorMessage className="align-items-center gap-3 lh-1px" message="Please choose completion date" />
                )}
                {checkPath && missingDocs.includes("post_graduate_diploma") ? (
                  <ErrorMessage
                    className="align-items-center"
                    message="Post Graduate Diploma document is required"
                  />
                ) : (
                  ""
                )}
              </div>
              <div className="bg-off-white min-h-1px"></div>
            </>
          )}
          {values.qualification === "bachelor_or_degree" && (
            <>
              <div className="d-flex flex-column gap-10 pgc_upload_container">
                <div className="fs-16 fw-700 text-black">
                  {EARLY_CHILDHOOD_EDUCATION.BACHELOR_OR_DEGREE}
                </div>
                <DocumentUpload
                  onFileSelect={handleFileSelect}
                  type={"bachelor_or_degree"}
                  candidate_uuid={
                    isSuperAdmin ? userpersonaldetails?.uuid : isImpersonating ? impersonationData?.candidate?.uuid : null
                  }
                  disabled={checkStageSubmittedOrNot(1)}
                />
                <div className="d-flex flex-column flex-md-row gap-10 align-items-md-center">
                  <div className="fs-13 fw-400 text-grey-dark text-nowrap">
                    Completion date:
                  </div>
                  <div className="min-w-200px">
                    <DatePicker
                      selected={
                        bachelorOrDegreeCertificate
                          ? ugValidUntil
                          : null
                      }
                      onChange={(date: Date | null) => {
                        setUgValidUntil(date);
                        handleDocDate("bachelor_or_degree", date);
                      }}
                      customInput={
                        <CustomDateInput
                          disabled={checkStageSubmittedOrNot(1)}
                          placeholder="Select a date..."
                        />
                      }
                      dateFormat="dd MMM yyyy"
                      maxDate={new Date()}
                      disabled={
                        !bachelorOrDegreeCertificate ||
                        checkStageSubmittedOrNot(1)
                      }
                      showYearDropdown
                      scrollableYearDropdown
                      yearDropdownItemNumber={50}
                    />
                  </div>
                </div>
                {(bachelorOrDegreeCertificate && !bachelor_or_degree_valid_until_str) && (
                  <ErrorMessage className="align-items-center gap-3 lh-1px" message="Please choose completion date" />
                )}
                {checkPath && missingDocs.includes("bachelor_or_degree") ? (
                  <ErrorMessage
                    className="align-items-center"
                    message="Bachelor or Degree document is required"
                  />
                ) : (
                  ""
                )}
              </div>
              <div className="bg-off-white min-h-1px"></div>
            </>
          )}

          {values.qualification === "diploma" && (
            <>
              <div className="d-flex flex-column gap-10 pgc_upload_container">
                <div className="fs-16 fw-700 text-black">
                  {EARLY_CHILDHOOD_EDUCATION.DIPLOMA}
                </div>
                <DocumentUpload
                  onFileSelect={handleFileSelect}
                  type={"diploma"}
                  candidate_uuid={
                    isSuperAdmin ? userpersonaldetails?.uuid : isImpersonating ? impersonationData?.candidate?.uuid : null
                  }
                  disabled={checkStageSubmittedOrNot(1)}
                />
                <div className="d-flex flex-column flex-md-row gap-10 align-items-md-center">
                  <div className="fs-13 fw-400 text-grey-dark text-nowrap">
                    Completion date:
                  </div>
                  <div className="min-w-200px">
                    <DatePicker
                      selected={
                        diplomaCertificate
                          ? diplValidUntil
                          : null
                      }
                      onChange={(date: Date | null) => {
                        setDiplValidUntil(date);
                        handleDocDate("diploma", date);
                      }}
                      customInput={
                        <CustomDateInput
                          disabled={checkStageSubmittedOrNot(1)}
                          placeholder="Select a date..."
                        />
                      }
                      dateFormat="dd MMM yyyy"
                      maxDate={new Date()}
                      disabled={
                        !diplomaCertificate ||
                        checkStageSubmittedOrNot(1)
                      }
                      showYearDropdown
                      scrollableYearDropdown
                      yearDropdownItemNumber={50}
                    />
                  </div>
                </div>
                {(diplomaCertificate && !diploma_valid_until_str) && (
                  <ErrorMessage className="align-items-center gap-3 lh-1px" message="Please choose completion date" />
                )}
                {checkPath &&  missingDocs.includes("diploma") ? (
                  <ErrorMessage
                    className="align-items-center"
                    message="Diploma document is required"
                  />
                ) : (
                  ""
                )}
              </div>
              <div className="bg-off-white min-h-1px"></div>
            </>
          )}

          {values.qualification === "certificate_iii" && (
            <>
              <div className="d-flex flex-column gap-10 ciii_upload_container">
                <div className="fs-16 fw-700 text-black">
                  {EARLY_CHILDHOOD_EDUCATION.CERTIFICATE_III}
                </div>
                <DocumentUpload
                  onFileSelect={handleFileSelect}
                  type={"certificate_iii"}
                  candidate_uuid={
                    isSuperAdmin ? userpersonaldetails?.uuid : isImpersonating ? impersonationData?.candidate?.uuid : null
                  }
                  disabled={checkStageSubmittedOrNot(1)}
                />
                <div className="d-flex flex-column flex-md-row gap-10 align-items-md-center">
                  <div className="fs-13 fw-400 text-grey-dark text-nowrap">
                    Completion date:
                  </div>
                  <div className="min-w-200px">
                    <DatePicker
                      selected={
                        certificateIIICertificate
                          ? ciiiValidUntil
                          : null
                      }
                      onChange={(date: Date | null) => {
                        setCiiiValidUntil(date);
                        handleDocDate("certificate_iii", date);
                      }}
                      customInput={
                        <CustomDateInput
                          disabled={checkStageSubmittedOrNot(1)}
                          placeholder="Select a date..."
                        />
                      }
                      dateFormat="dd MMM yyyy"
                      maxDate={new Date()}
                      disabled={
                        !certificateIIICertificate ||
                        checkStageSubmittedOrNot(1)
                      }
                      showYearDropdown
                      scrollableYearDropdown
                      yearDropdownItemNumber={50}
                    />
                  </div>
                </div>
                {(certificateIIICertificate && !certificate_iii_valid_until_str) && (
                  <ErrorMessage className="align-items-center gap-3 lh-1px" message="Please choose completion date" />
                )}
                {checkPath && missingDocs.includes("certificate_iii") ? (
                  <ErrorMessage
                    className="align-items-center"
                    message="Certificate III document is required"
                  />
                ) : (
                  ""
                )}
              </div>
              <div className="bg-off-white min-h-1px"></div>
            </>
          )}
          {values.qualification === "currently_studying" && (
            <>
              <div className="d-flex flex-column gap-10 course_progression_upload_container">
                <div className="fs-16 fw-700 text-black">
                  Course Progression Letter
                </div>
                <DocumentUpload
                  onFileSelect={handleFileSelect}
                  type={"course_progression_letter"}
                  candidate_uuid={
                    isSuperAdmin ? userpersonaldetails?.uuid : isImpersonating ? impersonationData?.candidate?.uuid : null
                  }
                  disabled={checkStageSubmittedOrNot(1)}
                />
                {checkPath && missingDocs.includes("course_progression_letter") ? (
                  <ErrorMessage
                    className="align-items-center"
                    message="Course Progression Letter is required"
                  />
                ) : (
                  ""
                )}
              </div>
              <div className="bg-off-white min-h-1px"></div>
            </>
          )}
          {!!values.acecqa_approved === true && (
            <>
              <div className="d-flex flex-column gap-10 acecqa_assessment_upload_container">
                <div className="fs-16 fw-700 text-black">ACECQA Assessment</div>
                <DocumentUpload
                  onFileSelect={handleFileSelect}
                  type={"ACECQA_assessment"}
                  candidate_uuid={
                    isSuperAdmin ? userpersonaldetails?.uuid : isImpersonating ? impersonationData?.candidate?.uuid : null
                  }
                  disabled={checkStageSubmittedOrNot(1)}
                />
                {checkPath && missingDocs.includes("ACECQA_assessment") ? (
                  <ErrorMessage
                    className="align-items-center"
                    message="ACECQA Assessment document is required"
                  />
                ) : (
                  ""
                )}
              </div>
              <div className="bg-off-white min-h-1px"></div>
            </>
          )}

          {selectedAlternativePosition &&
            selectedAlternativePosition.includes(
              ALTERNATIVE_POSITIONS.CHEF_COOK
            ) && (
              <>
                <div className="d-flex flex-column gap-10 fhc_upload_container">
                  <div className="fs-16 fw-700 text-black">
                    Cooking Qualification
                  </div>
                  <DocumentUpload
                    onFileSelect={handleFileSelect}
                    type={"cooking_qualification"}
                    candidate_uuid={
                      isSuperAdmin ? userpersonaldetails?.uuid : null
                    }
                    disabled={checkStageSubmittedOrNot(1)}
                  />
                  <div className="d-flex flex-column flex-md-row gap-10 align-items-md-center">
                    <div className="fs-13 fw-400 text-grey-dark text-nowrap">
                      Completion date:
                    </div>
                    <div className="min-w-200px">
                      <DatePicker
                        selected={
                          cookingQualificationCertificate
                            ? cqValidUntil
                            : null
                        }
                        onChange={(date: Date | null) => {
                          setCqValidUntil(date);
                          handleDocDate("cooking_qualification", date);
                        }}
                        customInput={
                          <CustomDateInput
                            disabled={checkStageSubmittedOrNot(1)}
                            placeholder="Select a date..."
                          />
                        }
                        dateFormat="dd MMM yyyy"
                        minDate={getMinDateByDocumentType("cooking_qualification")}
                        maxDate={new Date()}
                        disabled={
                          !cookingQualificationCertificate || checkStageSubmittedOrNot(1)
                        }
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={50}
                      />
                    </div>

                  </div>
                  {(cookingQualificationCertificate && !cooking_qualification_valid_until_str) && (
                    <ErrorMessage className="align-items-center" message="Please choose completion date" />
                  )}
                  {checkPath && missingDocs.includes("cooking_qualification") ? (
                    <ErrorMessage
                      className="align-items-center"
                      message="Cooking Qualification is required"
                    />
                  ) : (
                    ""
                  )}
                </div>
                <div className="bg-off-white min-h-1px"></div>
              </>
            )}

          {selectedAlternativePosition &&
            selectedAlternativePosition.includes(
              ALTERNATIVE_POSITIONS.CHEF_COOK
            ) && (
              <>
                <div className="d-flex flex-column gap-10 fhc_upload_container">
                  <div className="fs-16 fw-700 text-black">
                    Food Handler Certificate
                  </div>
                  <DocumentUpload
                    onFileSelect={handleFileSelect}
                    type={"food_handler_certificate"}
                    candidate_uuid={
                      isSuperAdmin ? userpersonaldetails?.uuid : isImpersonating ? impersonationData?.candidate?.uuid : null
                    }
                    disabled={checkStageSubmittedOrNot(1)}
                  />
                  <div className="d-flex flex-column flex-md-row gap-10 align-items-md-center">
                    <div className="fs-13 fw-400 text-grey-dark text-nowrap">
                      Issue date:
                    </div>
                    <div className="min-w-200px">
                      <DatePicker
                        selected={
                          foodHandlerCertificate
                            ? fhcValidUntil
                            : null
                        }
                        onChange={(date: Date | null) => {
                          setFhcValidUntil(date);
                          handleDocDate("food_handler_certificate", date);
                        }}
                        customInput={
                          <CustomDateInput
                            disabled={checkStageSubmittedOrNot(1)}
                            placeholder="Select a date..."
                          />
                        }
                        dateFormat="dd MMM yyyy"
                        minDate={getMinDateByDocumentType("food_handler_certificate")}
                        maxDate={new Date()}
                        disabled={
                          !foodHandlerCertificate || checkStageSubmittedOrNot(1)
                        }
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={15}
                      />
                    </div>
                    <div className="d-flex flex-row fs-13 fw-400 text-grey-dark gap-3">
                      <Warning width={"15"} height={"15"} color={"#464646"} />
                        Expires {getValidityMonths('food_handler_certificate')} months after issue date and will need renewing
                    </div>
                  </div>
                  {(foodHandlerCertificate && !food_handler_certificate_valid_until_str) && (
                    <ErrorMessage className="align-items-center" message="Please select the date this document was issued" />
                  )}
                  {checkPath && missingDocs.includes("food_handler_certificate") ? (
                    <ErrorMessage
                      className="align-items-center"
                      message="Food Handler Certificate is required"
                    />
                  ) : (
                    ""
                  )}
                </div>
                <div className="bg-off-white min-h-1px"></div>
              </>
            )}
          
          <div className="qualification_button_container d-flex flex-column flex-lg-row gap-20 align-items-lg-center">
            {!isSuperAdmin && (
              <CustomButton
                type="submit"
                variant="primary"
                disabled={isDisabled}
                onClick={() => {
                  handleSubmit();
                }}
                className={`btn-theme ${
                  isDisabled
                    ? "text-grey-medium bg-off-white"
                    : "bg-teal text-black"
                }`}
              >
                Next
                <RightArrow color={isDisabled ? "#757575" : "#000000"} />
              </CustomButton>
            )}
            <div className="d-none d-lg-flex flex-lg-column fs-13 fw-400 text-grey-dark">
              <div>
                Please select your Qualification level and/or Alternative
                Positions.
              </div>
              <div>
                You can skip the Key Documents and add to your profile later.
              </div>
            </div>

            <div className="d-lg-none fs-13 fw-400 text-grey-dark">
              <div>
                Please select your Qualification level and/or Alternative
                Positions. You can skip the Key Documents and add to your
                profile later.
              </div>
            </div>
          </div>
        </div>
      </div>
    
  );
};

export default Qualifications;
